import React, { forwardRef } from "react";
import Button from "./Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBell } from "@fortawesome/free-solid-svg-icons";
import { Search, Bell, LogOut, Menu, X } from "lucide-react";
import PropTypes from "prop-types";
import {
  faHeart,
  faComment,
  faBookmark,
  faShare,
  faEllipsisH,
  faHome,
  faCommentDots,
  faUsers,
  faPlus,
  faTimes,
  faCompass,
  faSearch,
  faUser,
  faClipboard,
  faHandshake,
  faStar,
  faCamera,
  faUserCircle,
  faBars,
  faUserFriends,
  faClapperboard,
  faPenToSquare,
  faPen,
} from "@fortawesome/free-solid-svg-icons";

// Centralized default settings for buttons
const DEFAULT_SETTINGS = {
  size: 40,
  fontSize: 14,
  justifyContent: "center",
  // background: "black",
  color: "rgb(231, 218, 218)",
  hoverColor: "none",
  iconColor: "white", // Default icon color
  labelColor: "black", // Default label color
};



// Button Factory
const createButton = ({
  icon,
  label = "",
  title,
  background = DEFAULT_SETTINGS.background,
  color = DEFAULT_SETTINGS.color,
  hoverColor = DEFAULT_SETTINGS.hoverColor,
  size = DEFAULT_SETTINGS.size,
  fontSize = DEFAULT_SETTINGS.fontSize,
  widthLength,
  justifyContent = DEFAULT_SETTINGS.justifyContent,
  shape = "circle",
  iconColor = DEFAULT_SETTINGS.iconColor, // Default icon color
  labelColor = DEFAULT_SETTINGS.labelColor, // Default label color
  onClick,
  className,
  marginTop,
  marginleft,
}) => {
  return forwardRef((props, ref) => (
    <Button
      ref={ref}
      className={className}
      label={label}
      icon={<FontAwesomeIcon icon={icon} />}
      color={color}
      background={background}
      hoverColor={hoverColor}
      size={size}
      fontSize={fontSize}
      widthLength={widthLength || (shape === "oval" ? 120 : size)}
      justifyContent={justifyContent}
      shape={shape}
      title={title || label}
      iconColor={iconColor} // Pass iconColor
      labelColor={labelColor} // Pass labelColor
      onClick={onClick}
      {...props} // Allow overriding props when needed
    />
  ));
};

// Button Components
export const LoveButton = createButton({
  icon: faHeart,
  title: "Love",
  color: "#e74c3c",
  className: "love-button",
  iconColor: "white", // Set custom icon color
});

export const CommentButton = createButton({
  icon: faComment,
  title: "Comment",
  color: "#3498db",
  className: "comment-button",
  iconColor: "white", // Set custom icon color
});

export const SaveButton = createButton({
  icon: faBookmark,
  title: "Save",
  color: "#6446a4",
  className: "save-button",
  iconColor: "white", // Set custom icon color
});

export const ShareButton = createButton({
  icon: faShare,
  title: "Share",
  color: "#2ecc71",
  className: "share-button",
  iconColor: "white", // Set custom icon color
});

export const MoreButton = createButton({
    icon: faEllipsisH,
    title: "More Options",
    className: "home-button",
    color:"#2f2c2c"
  });

export const HomeButton = createButton({
  icon: faHome,
  label: "Home",
  shape: "oval",
  title: "Home",
  className: "home-button",
  justifyContent:"unset",
  color:"#363133",
  background: "#efe2e7",
});

export const ReelsButton = createButton({
  icon: faClapperboard, // Reels icon
  label: "Reels",
  shape: "oval",
  title: "Reels",
  className: "reels-button",
  justifyContent: "unset"
});

export const CreatePostButton = createButton({
  icon: faPenToSquare, // Create Post icon (📝)
  label: "Create Post",
  shape: "oval",
  title: "Create a New Post",
  className: "create-post-button",
  justifyContent: "unset"
});

export const MessageButton = createButton({
  icon: faCommentDots,
  label: "Messaging",
  shape: "oval",
  title: "Messaging",
  className: "messaging-button",
  justifyContent:"unset",
});

export const FriendsButton = createButton({
  icon: faUserFriends,
  label: "Friends",
  shape: "oval",
  title: "Friends",
  className: "friends-button",
  justifyContent:"unset",
  color:"#6a6c6d"
});

export const GroupButton = createButton({
  icon: faUsers,
  label: "Group",
  shape: "oval",
  title: "Group",
  className: "group-button",
  justifyContent:"unset",
});

export const ExploreButton = createButton({
  icon: faCompass,
  label: "Explore",
  shape: "oval",
  title: "Explore",
  className: "explore-button",
  justifyContent:"unset",
  color:"#6a6c6d"
});

export const CreateButton = createButton({
  icon: faPlus,
  title: "Create",
  color: "#232223",
  className: "create-button",
});

export const CloseButton = createButton({
  icon: faTimes,
  title: "Close",
  color: "#e74c3c",
  className: "close-button",
});

export const CameraButton = createButton({
  icon: faCamera,
  title: "Camera",
  color: "#e74c3c",
  className: "camera-button",
});

export const ProfileButton = createButton({
  icon: faUserCircle,
  label: "Profile",
  shape: "oval",
  title: "Profile",
  className: "profile-button",
  justifyContent:"unset",
  color:"#6a6c6d"
});

export const AdmirerButton = createButton({
  icon: faStar,
  label: "Admirer",
  shape: "oval",
  title: "Admirer",
  className: "admirer-button",
  justifyContent:"unset",
});

export const EditButton = createButton({
  icon: faPen, // Edit icon (🖊️)
  label: "Edit",
  shape: "oval",
  title: "Edit Post",
  className: "edit-button",
  justifyContent: "unset",
});

export const ThreeBarButton = createButton({
  icon: faBars,
  title: "More options",
  className: "three-bar-button",
  justifyContent: "unset",
  color: "#6a6c6d",
  justifyContent: "center",
});

export const ResetPasswordButton = createButton({
  label: "Reset Password",
  shape: "oval",
  color: "#7f0606",
  title: "Reset your Password",
  className: "reset-password-button",
  justifyContent:"unset",
  widthLength: 100,
  justifyContent: "center",
});

export const PostButton = createButton({
  icon: faClipboard,
  title: "Post",
  color: "#2ecc71",
  className: "post-button",
});

export const CollaborateButton = createButton({
  icon: faHandshake,
  title: "Collaborate",
  className: "collaborate-button",
  justifyContent:"unset",
});

export const DefaultUserIconButton = createButton({
  icon: faUser,
  title: "User",
  className: "user-button",
});

export const NotificationButton = createButton({
  icon: Bell, // Assuming faBell is imported from FontAwesome
  title: "Notifications",
  className: "notification-button",
  size: 18,
});

export const SearchButton = createButton({
  icon: Search,
  title: "Search",
  className: "search-button",
  size: 18,
  color: "white",          // icon color
});
